<template>
  <div class="financial-categories-management">
    <div class="page-header">
      <h2>收支分类管理</h2>
      <div class="header-actions">
        <a-button type="primary" @click="handleSave" :loading="saving">
          <template #icon>
            <SaveOutlined />
          </template>
          保存
        </a-button>
        <a-button @click="handleRefresh" :loading="loading">
          <template #icon>
            <ReloadOutlined />
          </template>
          刷新
        </a-button>
      </div>
    </div>

    <a-card>
      <div class="tree-container">
        <div v-if="loading" class="loading-container">
          <a-spin size="large" />
        </div>
        
        <div v-else-if="categories.length === 0" class="empty-container">
          <a-empty description="暂无分类数据">
            <a-button type="primary" @click="addRootCategory">
              <template #icon>
                <PlusOutlined />
              </template>
              添加一级分类
            </a-button>
          </a-empty>
        </div>
        
        <div v-else class="tree-content">
          <div class="tree-actions">
            <a-button type="dashed" @click="addRootCategory">
              <template #icon>
                <PlusOutlined />
              </template>
              添加一级分类
            </a-button>
          </div>
          
          <div class="category-tree">
            <div
              v-for="(category, index) in categories"
              :key="category.key"
              class="category-level-1"
              :class="{ 
                'dragging': draggingIndex === index && draggingType === 'root',
                'drag-over': dragOverIndex === index && dragOverType === 'root' && draggingType === 'root'
              }"
              @dragover.prevent="handleDragOver($event, index, 'root')"
              @dragenter.prevent="handleDragEnter($event, index, 'root')"
              @drop.prevent="handleDrop($event, index, 'root')"
            >
              <div class="category-item">
                <div class="category-content">
                  <div 
                    class="drag-handle" 
                    title="拖拽排序"
                    draggable="true"
                    @dragstart="handleDragStart($event, index, 'root')"
                    @dragend="handleDragEnd"
                  >☰</div>
                  <a-input
                    v-model:value="category.name"
                    placeholder="请输入分类名称"
                    :class="{ 'input-error': !category.name }"
                    @change="markAsChanged"
                  />
                </div>
                <div class="category-actions">
                  <a-button type="text" size="small" @click="addSubCategory(category)">
                    <template #icon>
                      <PlusOutlined />
                    </template>
                    添加子分类
                  </a-button>
                  <a-popconfirm
                    title="确定要删除这个分类吗？"
                    @confirm="removeCategory(index)"
                  >
                    <a-button type="text" size="small" danger>
                      <template #icon>
                        <DeleteOutlined />
                      </template>
                      删除
                    </a-button>
                  </a-popconfirm>
                </div>
              </div>
              
              <div v-if="category.children && category.children.length > 0" class="sub-categories">
                <div
                  v-for="(subCategory, subIndex) in category.children"
                  :key="subCategory.key"
                  class="category-level-2"
                  @dragover.prevent="handleSubDragOver($event, subIndex, index)"
                  @dragenter.prevent="handleSubDragEnter($event, subIndex, index)"
                  @drop.prevent="handleSubDrop($event, subIndex, index)"
                  :class="{ 
                    'dragging': draggingIndex === subIndex && draggingType === 'sub' && draggingParentIndex === index,
                    'drag-over': dragOverIndex === subIndex && dragOverType === 'sub' && dragOverParentIndex === index && draggingType === 'sub' && draggingParentIndex === index
                  }"
                >
                  <div class="category-item">
                    <div class="category-content">
                      <div 
                        class="drag-handle" 
                        title="拖拽排序"
                        draggable="true"
                        @dragstart="handleSubDragStart($event, subIndex, index)"
                        @dragend="handleDragEnd"
                      >☰</div>
                      <div class="sub-category-prefix">└─</div>
                      <a-input
                        v-model:value="subCategory.name"
                        placeholder="请输入子分类名称"
                        :class="{ 'input-error': !subCategory.name }"
                        @change="markAsChanged"
                      />
                    </div>
                    <div class="category-actions">
                      <a-popconfirm
                        title="确定要删除这个子分类吗？"
                        @confirm="removeSubCategory(category, subIndex)"
                      >
                        <a-button type="text" size="small" danger>
                          <template #icon>
                            <DeleteOutlined />
                          </template>
                          删除
                        </a-button>
                      </a-popconfirm>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </a-card>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { message, Modal } from 'ant-design-vue'
import {
  SaveOutlined,
  ReloadOutlined,
  PlusOutlined,
  DeleteOutlined
} from '@ant-design/icons-vue'
import { financialCategoryApi } from '@/api/financialCategory'

export default {
  name: 'FinancialCategories',
  components: {
    SaveOutlined,
    ReloadOutlined,
    PlusOutlined,
    DeleteOutlined
  },
  setup() {
    const loading = ref(false)
    const saving = ref(false)
    const categories = ref([])
    const hasChanges = ref(false)
    const draggingIndex = ref(null)
    const draggingType = ref(null)
    const draggingParentIndex = ref(null)
    const dragOverIndex = ref(null)
    const dragOverType = ref(null)
    const dragOverParentIndex = ref(null)
    const isDraggingSubcategory = ref(false)
    let keyCounter = 1

    const generateKey = () => `temp_${keyCounter++}`

    const loadCategories = async () => {
      loading.value = true
      try {
        const result = await financialCategoryApi.getTree()
        categories.value = result.data.map(category => ({
          ...category,
          key: category.id ? `cat_${category.id}` : generateKey(),
          status: 1,
          children: category.children ? category.children.map(child => ({
            ...child,
            key: child.id ? `cat_${child.id}` : generateKey(),
            status: 1
          })) : []
        }))
        hasChanges.value = false
      } catch (error) {
        console.error('加载收支分类失败:', error)
        message.error('加载收支分类失败')
      } finally {
        loading.value = false
      }
    }

    const addRootCategory = () => {
      categories.value.push({
        key: generateKey(),
        name: '',
        status: 1,
        children: []
      })
      markAsChanged()
    }

    const addSubCategory = (parentCategory) => {
      if (!parentCategory.children) {
        parentCategory.children = []
      }
      parentCategory.children.push({
        key: generateKey(),
        name: '',
        status: 1
      })
      markAsChanged()
    }

    const removeCategory = async (index) => {
      const category = categories.value[index]
      
      // 如果分类有ID（已保存到数据库），调用删除API
      if (category.id) {
        try {
          await financialCategoryApi.delete(category.id)
          message.success('删除成功')
          loadCategories() // 重新加载分类列表
        } catch (error) {
          console.error('删除收支分类失败:', error)
          message.error(`删除失败: ${error.message || '未知错误'}`)
        }
      } else {
        // 如果是新创建未保存的分类，直接从数组中移除
        categories.value.splice(index, 1)
        markAsChanged()
      }
    }

    const removeSubCategory = async (parentCategory, subIndex) => {
      const subCategory = parentCategory.children[subIndex]
      
      // 如果子分类有ID（已保存到数据库），调用删除API
      if (subCategory.id) {
        try {
          await financialCategoryApi.delete(subCategory.id)
          message.success('删除成功')
          loadCategories() // 重新加载分类列表
        } catch (error) {
          console.error('删除子分类失败:', error)
          message.error(`删除失败: ${error.message || '未知错误'}`)
        }
      } else {
        // 如果是新创建未保存的子分类，直接从数组中移除
        parentCategory.children.splice(subIndex, 1)
        markAsChanged()
      }
    }

    const markAsChanged = () => {
      hasChanges.value = true
    }

    const validateCategories = () => {
      for (const category of categories.value) {
        if (!category.name?.trim()) {
          message.error('请填写所有分类名称')
          return false
        }
        if (category.children) {
          for (const child of category.children) {
            if (!child.name?.trim()) {
              message.error('请填写所有子分类名称')
              return false
            }
          }
        }
      }
      return true
    }

    const handleSave = async () => {
      if (!hasChanges.value) {
        message.info('没有需要保存的更改')
        return
      }

      if (!validateCategories()) {
        return
      }

      saving.value = true
      try {
        const saveData = {
          type: 'financial',
          categories: categories.value.map((category, index) => ({
            id: category.id,
            name: category.name,
            status: category.status,
            orderNum: index + 1,
            children: category.children?.map((child, childIndex) => ({
              id: child.id,
              name: child.name,
              status: child.status,
              orderNum: childIndex + 1
            })) || []
          }))
        }

        await financialCategoryApi.batchUpdate(saveData)
        message.success('保存成功')
        hasChanges.value = false
        loadCategories()
      } catch (error) {
        console.error('保存失败:', error)
        message.error(`保存失败: ${error.message || '未知错误'}`)
      } finally {
        saving.value = false
      }
    }

    const handleRefresh = () => {
      if (hasChanges.value) {
        Modal.confirm({
          title: '确认刷新',
          content: '有未保存的更改，确定要刷新吗？',
          onOk: () => loadCategories()
        })
      } else {
        loadCategories()
      }
    }

    const handleDragStart = (event, index, type, parentIndex = null) => {
      console.log('ROOT Drag start:', { index, type, parentIndex })
      event.stopPropagation() // 阻止事件冒泡
      draggingIndex.value = index
      draggingType.value = type
      draggingParentIndex.value = parentIndex
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', JSON.stringify({ index, type, parentIndex }))
    }

    const handleSubDragStart = (event, subIndex, parentIndex) => {
      console.log('Sub drag start:', { subIndex, parentIndex })
      event.stopPropagation() // 阻止事件冒泡到父分类
      isDraggingSubcategory.value = true
      draggingIndex.value = subIndex
      draggingType.value = 'sub'
      draggingParentIndex.value = parentIndex
      event.dataTransfer.effectAllowed = 'move'
      event.dataTransfer.setData('text/plain', JSON.stringify({ index: subIndex, type: 'sub', parentIndex }))
    }

    const handleSubDragEnter = (event, subIndex, parentIndex) => {
      console.log('Sub drag enter:', { subIndex, parentIndex, dragging: { index: draggingIndex.value, type: draggingType.value, parent: draggingParentIndex.value } })
      
      // 如果正在拖拽父分类，则不阻止事件冒泡
      if (draggingType.value === 'root') {
        return
      }
      
      // 只有同一父分类下的子分类才显示拖拽目标
      if (draggingType.value === 'sub' && draggingParentIndex.value === parentIndex) {
        event.stopPropagation()
        dragOverIndex.value = subIndex
        dragOverType.value = 'sub'
        dragOverParentIndex.value = parentIndex
      }
    }

    const handleSubDragOver = (event, subIndex, parentIndex) => {
      // 如果正在拖拽父分类，则不阻止事件冒泡，让父分类处理
      if (draggingType.value === 'root') {
        console.log('Sub drag over - ROOT DRAGGING, letting parent handle:', { subIndex, parentIndex })
        // 不设置 stopPropagation，让事件冒泡到父分类
        return
      }
      
      // 只有同一父分类下的子分类才允许拖拽
      if (draggingType.value === 'sub' && draggingParentIndex.value === parentIndex) {
        console.log('Sub drag over - VALID:', { subIndex, parentIndex, draggingType: draggingType.value })
        event.stopPropagation() // 只有子分类拖拽时才阻止冒泡
        event.dataTransfer.dropEffect = 'move'
      } else {
        console.log('Sub drag over - INVALID:', { subIndex, parentIndex, draggingType: draggingType.value, draggingParent: draggingParentIndex.value })
        event.dataTransfer.dropEffect = 'none'
      }
    }

    const handleSubDrop = (event, targetIndex, targetParentIndex) => {
      console.log('Sub drop:', { targetIndex, targetParentIndex, dragging: { index: draggingIndex.value, type: draggingType.value, parent: draggingParentIndex.value } })
      
      // 如果正在拖拽父分类，则不阻止事件冒泡，让父分类处理
      if (draggingType.value === 'root') {
        console.log('Sub drop - ROOT DRAGGING, letting parent handle')
        return
      }
      
      if (draggingIndex.value === null || draggingType.value !== 'sub') {
        console.log('No sub dragging data')
        return
      }
      
      event.stopPropagation() // 只有子分类拖拽时才阻止冒泡
      
      if (draggingParentIndex.value !== targetParentIndex) {
        console.log('Different parent for subcategory, not allowed')
        return
      }
      
      if (draggingIndex.value === targetIndex) {
        console.log('Same position')
        return
      }
      
      console.log('Performing sub drag operation...')
      
      // 子分类拖拽
      const parentCategory = categories.value[targetParentIndex]
      if (parentCategory && parentCategory.children) {
        const draggedItem = parentCategory.children.splice(draggingIndex.value, 1)[0]
        parentCategory.children.splice(targetIndex, 0, draggedItem)
        console.log('Subcategory moved successfully')
        markAsChanged()
      } else {
        console.log('Parent category not found or no children array')
      }
      
      // 清除拖拽状态
      dragOverIndex.value = null
      dragOverType.value = null
      dragOverParentIndex.value = null
    }

    const handleDragEnter = (event, index, type, parentIndex = null) => {
      console.log('ROOT Drag enter:', { index, type, parentIndex, dragging: { index: draggingIndex.value, type: draggingType.value, parent: draggingParentIndex.value } })
      event.stopPropagation() // 阻止事件冒泡
      // 只有同级才显示拖拽目标
      if (draggingType.value === type && draggingParentIndex.value === parentIndex) {
        dragOverIndex.value = index
        dragOverType.value = type
        dragOverParentIndex.value = parentIndex
      }
    }

    const handleDragOver = (event, index, type, parentIndex = null) => {
      console.log('ROOT drag over:', { index, type, draggingType: draggingType.value })
      event.stopPropagation() // 阻止事件冒泡
      // 只有同级才允许拖拽
      if (draggingType.value === type && draggingParentIndex.value === parentIndex) {
        event.dataTransfer.dropEffect = 'move'
      } else {
        event.dataTransfer.dropEffect = 'none'
      }
    }

    const handleDrop = (event, targetIndex, targetType, targetParentIndex = null) => {
      console.log('ROOT Drop:', { targetIndex, targetType, targetParentIndex, dragging: { index: draggingIndex.value, type: draggingType.value, parent: draggingParentIndex.value } })
      event.stopPropagation() // 阻止事件冒泡
      
      if (draggingIndex.value === null || draggingType.value === null) {
        console.log('No dragging data')
        return
      }
      
      // 只允许同级拖拽
      if (draggingType.value !== targetType) {
        console.log('Different type, not allowed')
        return
      }
      
      if (draggingType.value === 'sub' && draggingParentIndex.value !== targetParentIndex) {
        console.log('Different parent for subcategory, not allowed')
        return
      }
      
      if (draggingIndex.value === targetIndex) {
        console.log('Same position')
        return
      }
      
      console.log('Performing ROOT drag operation...')
      
      if (targetType === 'root') {
        // 根级分类拖拽
        const draggedItem = categories.value.splice(draggingIndex.value, 1)[0]
        categories.value.splice(targetIndex, 0, draggedItem)
        console.log('Root category moved successfully')
        markAsChanged()
      }
      
      // 清除拖拽状态
      dragOverIndex.value = null
      dragOverType.value = null
      dragOverParentIndex.value = null
    }

    const handleDragEnd = () => {
      console.log('Drag end')
      draggingIndex.value = null
      draggingType.value = null
      draggingParentIndex.value = null
      dragOverIndex.value = null
      dragOverType.value = null
      dragOverParentIndex.value = null
      isDraggingSubcategory.value = false
    }

    onMounted(() => {
      loadCategories()
    })

    return {
      loading,
      saving,
      categories,
      hasChanges,
      draggingIndex,
      draggingType,
      draggingParentIndex,
      dragOverIndex,
      dragOverType,
      dragOverParentIndex,
      isDraggingSubcategory,
      addRootCategory,
      addSubCategory,
      removeCategory,
      removeSubCategory,
      handleSave,
      handleRefresh,
      markAsChanged,
      handleDragStart,
      handleSubDragStart,
      handleSubDragEnter,
      handleSubDragOver,
      handleSubDrop,
      handleDragEnter,
      handleDragOver,
      handleDrop,
      handleDragEnd
    }
  }
}
</script>

<style scoped>
.financial-categories-management {
  padding: 0;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h2 {
  margin: 0;
}

.header-actions {
  display: flex;
  gap: 8px;
}

.tree-container {
  min-height: 400px;
}

.loading-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.empty-container {
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
}

.tree-content {
  width: 100%;
}

.tree-actions {
  margin-bottom: 16px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f0f0f0;
}

.category-tree {
  width: 100%;
}

.category-level-1 {
  margin-bottom: 16px;
  border: 1px solid #f0f0f0;
  border-radius: 6px;
  overflow: hidden;
}

.category-level-2 {
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

.category-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
}

.category-content {
  display: flex;
  align-items: center;
  flex: 1;
}

.sub-category-prefix {
  color: #999;
  margin-right: 8px;
  font-family: monospace;
}

.category-actions {
  display: flex;
  gap: 8px;
  margin-left: 16px;
}

.input-error {
  border-color: #ff4d4f;
}

.input-error:hover {
  border-color: #ff4d4f;
}

.input-error:focus {
  border-color: #ff4d4f;
  box-shadow: 0 0 0 2px rgba(255, 77, 79, 0.2);
}

.sub-categories {
  border-top: 1px solid #f0f0f0;
}

.category-level-1, .category-level-2 {
  cursor: grab;
  transition: all 0.2s ease;
}

.category-level-1:active, .category-level-2:active {
  cursor: grabbing;
}

.category-level-1.dragging, .category-level-2.dragging {
  opacity: 0.5;
  transform: scale(0.98);
}

.category-level-1.drag-over {
  border: 2px dashed #1890ff;
  background-color: #f0f8ff;
}

.category-level-2.drag-over {
  border: 2px dashed #52c41a;
  background-color: #f6ffed;
}

.category-level-1:hover:not(.dragging), .category-level-2:hover:not(.dragging) {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.category-item {
  position: relative;
}

.category-item .category-content {
  pointer-events: none;
}

.category-item .category-content * {
  pointer-events: all;
}

.category-item .category-actions {
  pointer-events: all;
}

.drag-handle {
  color: #999;
  cursor: grab;
  padding: 4px 8px;
  margin-right: 8px;
  border-radius: 4px;
  transition: all 0.2s ease;
  user-select: none;
  font-size: 14px;
  line-height: 1;
}

.drag-handle:hover {
  color: #1890ff;
  background-color: #f0f8ff;
}

.drag-handle:active {
  cursor: grabbing;
}
</style>